import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  BadgeCheck,
  BarChart3,
  FileText,
  ListChecks,
  Send,
  ShieldCheck,
  Undo2,
} from 'lucide-react';

export const Route = createFileRoute('/_app/(mydata)/mydata/')({
  component: RouteComponent,
});

function Feature({
  icon: Icon,
  title,
  children,
  className = '',
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-zinc-200/70 bg-white p-4 shadow-xs dark:border-zinc-800 dark:bg-zinc-900 ${className}`}
    >
      <div className='mb-2 flex items-center gap-2'>
        <Icon className='h-5 w-5' />
        <h3 className='text-sm font-semibold'>{title}</h3>
      </div>
      <div className='text-sm text-zinc-600 dark:text-zinc-300'>{children}</div>
    </div>
  );
}

function RouteComponent() {
  return (
    <div className='mx-auto max-w-5xl p-4 md:p-8'>
      {/* Header */}
      <div className='mb-6 flex items-start justify-between gap-3'>
        <div>
          <h1 className='mb-2 flex items-center gap-2 text-2xl font-semibold'>
            <FileText className='h-6 w-6' />
            MyDATA Διαβίβαση Παραστατικών
            <span className='rounded-full bg-green-100 px-2 py-[2px] text-[10px] font-semibold leading-none text-green-700 dark:bg-green-500/20 dark:text-green-200'>
              Έρχεται σύντομα
            </span>
          </h1>
          <p className='max-w-2xl text-sm text-zinc-600 dark:text-zinc-300'>
            Αυτόματη αποστολή παραστατικών στην ΑΑΔΕ (myDATA) με πλήρη παρακολούθηση και ασφάλεια.
            Το σύστημα αναλαμβάνει να δημιουργεί, να στέλνει και να παρακολουθεί τα παραστατικά σας,
            χωρίς χειροκίνητες ενέργειες και χωρίς λάθη.
          </p>
        </div>
      </div>

      {/* Τι είναι το myDATA */}
      <section className='mb-10'>
        <h2 className='mb-3 text-base font-semibold'>Τι είναι το myDATA</h2>
        <p className='text-sm text-zinc-700 dark:text-zinc-300'>
          Το myDATA είναι η ηλεκτρονική πλατφόρμα της ΑΑΔΕ για την καταγραφή όλων των τιμολογίων και
          αποδείξεων. Από το 2021, όλες οι επιχειρήσεις υποχρεούνται να διαβιβάζουν τα παραστατικά
          τους ηλεκτρονικά ώστε να ενημερώνονται αυτόματα τα ηλεκτρονικά τους βιβλία.
        </p>
      </section>

      {/* Features */}
      <section className='mb-8'>
        <h2 className='mb-3 text-base font-semibold'>Τι θα μπορείτε να κάνετε</h2>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <Feature icon={Send} title='Αυτόματη Διαβίβαση'>
            Με κάθε νέα απόδειξη ή τιμολόγιο, το σύστημα δημιουργεί το σωστό αρχείο και το στέλνει
            στην ΑΑΔΕ.
          </Feature>
          <Feature icon={BadgeCheck} title='MARK & Κατάσταση'>
            Εμφάνιση του MARK και της κατάστασης (Sent / Pending / Rejected / Cancelled) μέσα στο
            ERP.
          </Feature>
          <Feature icon={Undo2} title='Ακύρωση & Πιστωτικά'>
            Δημιουργία πιστωτικών και ακύρωση παραστατικών με αναφορά στο MARK του αρχικού.
          </Feature>
          <Feature icon={ListChecks} title='Dashboard Αποστολών'>
            Ενιαίο panel όπου βλέπετε όλα τα παραστατικά, τα status και τα σφάλματα.
          </Feature>
          <Feature icon={BarChart3} title='Αναφορές Λογιστή'>
            Εξαγωγή MARK και παραστατικών σε CSV/Excel για άμεση συμφωνία με τον λογιστή σας.
          </Feature>
          <Feature icon={ShieldCheck} title='Ασφαλής Σύνδεση'>
            Επικοινωνία με το REST API της ΑΑΔΕ με τα επίσημα credentials του πελάτη.
          </Feature>
        </div>
      </section>

      {/* How it works */}
      <section className='mb-10'>
        <h2 className='mb-3 text-base font-semibold'>Πώς θα λειτουργεί</h2>
        <ol className='list-decimal space-y-2 pl-5 text-sm text-zinc-700 dark:text-zinc-300'>
          <li>Ο διαχειριστής εκδίδει παραστατικό στην εφαρμογή.</li>
          <li>Το σύστημα δημιουργεί το XML και το στέλνει αυτόματα στο myDATA.</li>
          <li>
            Η ΑΑΔΕ επιστρέφει <strong>MARK</strong> και το παραστατικό χαρακτηρίζεται ως
            «Απεσταλμένο».
          </li>
          <li>Αν απορριφθεί, εμφανίζεται στο dashboard και μπορεί να επανυποβληθεί.</li>
          <li>Ο λογιστής βλέπει όλες τις αποστολές συγκεντρωτικά.</li>
        </ol>
      </section>

      {/* Benefits */}
      <section className='mb-10'>
        <h2 className='mb-3 text-base font-semibold'>Τι κερδίζετε</h2>
        <ul className='list-disc space-y-1 pl-5 text-sm text-zinc-700 dark:text-zinc-300'>
          <li>Καμία διπλή καταχώριση στον λογιστή</li>
          <li>Μηδενισμός λαθών ή παραλείψεων</li>
          <li>Πλήρης διαφάνεια και ιστορικό</li>
          <li>Συμμόρφωση με την ΑΑΔΕ χωρίς κόπο</li>
          <li>Λιγότερα email / excel / τηλεφωνήματα με τον λογιστή</li>
        </ul>
      </section>
    </div>
  );
}
